import React, { useEffect, useRef } from 'react';

const ScrollResponsiveHeroGraphic = () => {
  const canvasRef = useRef(null);
  const scrollYRef = useRef(0);
  const rafIdRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let width = window.innerWidth;
    let height = window.innerHeight;

    // Set canvas size
    const setCanvasSize = () => {
      const dpr = window.devicePixelRatio || 1;
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.scale(dpr, dpr);
    };

    setCanvasSize();

    // Geometric layer definitions
    const layers = [
      // Background layer - slowest
      {
        shapes: [
          { type: 'circle', x: width * 0.2, y: height * 0.3, radius: 200, opacity: 0.06 },
          { type: 'rect', x: width * 0.75, y: height * 0.5, width: 350, height: 350, rotation: 15, opacity: 0.04 },
          { type: 'circle', x: width * 0.5, y: height * 0.8, radius: 150, opacity: 0.05 }
        ],
        speed: 0.2,
        rotationSpeed: 0.03
      },
      // Mid-back layer
      {
        shapes: [
          { type: 'circle', x: width * 0.65, y: height * 0.25, radius: 140, opacity: 0.08 },
          { type: 'rect', x: width * 0.25, y: height * 0.65, width: 220, height: 220, rotation: 45, opacity: 0.07 },
          { type: 'line', x1: width * 0.4, y1: height * 0.2, x2: width * 0.6, y2: height * 0.3, opacity: 0.06, width: 3 }
        ],
        speed: 0.35,
        rotationSpeed: 0.05
      },
      // Mid-front layer
      {
        shapes: [
          { type: 'circle', x: width * 0.82, y: height * 0.68, radius: 100, opacity: 0.11 },
          { type: 'rect', x: width * 0.1, y: height * 0.45, width: 160, height: 160, rotation: 60, opacity: 0.09 },
          { type: 'line', x1: width * 0.5, y1: height * 0.5, x2: width * 0.7, y2: height * 0.6, opacity: 0.08, width: 2 }
        ],
        speed: 0.55,
        rotationSpeed: 0.08
      },
      // Front layer - fastest
      {
        shapes: [
          { type: 'circle', x: width * 0.15, y: height * 0.75, radius: 70, opacity: 0.13 },
          { type: 'rect', x: width * 0.88, y: height * 0.32, width: 140, height: 140, rotation: 30, opacity: 0.12 },
          { type: 'circle', x: width * 0.45, y: height * 0.15, radius: 50, opacity: 0.1 }
        ],
        speed: 0.8,
        rotationSpeed: 0.12
      }
    ];

    // Draw function
    const draw = () => {
      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      // Calculate scroll factor (0 to 1 based on hero section)
      const scrollFactor = Math.min(scrollYRef.current / height, 1);

      // Draw each layer
      layers.forEach((layer, layerIndex) => {
        // Apply blur based on layer depth (background layers more blurred)
        const blurAmount = (layers.length - layerIndex - 1) * 0.5;
        ctx.filter = `blur(${blurAmount}px)`;

        layer.shapes.forEach(shape => {
          // Calculate parallax offset
          const parallaxOffset = scrollFactor * layer.speed * 100;
          const rotationOffset = scrollFactor * layer.rotationSpeed * 10;

          ctx.save();

          if (shape.type === 'circle') {
            ctx.beginPath();
            ctx.arc(
              shape.x,
              shape.y + parallaxOffset,
              shape.radius,
              0,
              Math.PI * 2
            );
            // Gradient fill for depth
            const gradient = ctx.createRadialGradient(
              shape.x, shape.y + parallaxOffset, 0,
              shape.x, shape.y + parallaxOffset, shape.radius
            );
            gradient.addColorStop(0, `rgba(255, 255, 255, ${shape.opacity * (1 - scrollFactor * 0.5)})`);
            gradient.addColorStop(1, `rgba(255, 255, 255, ${shape.opacity * 0.3 * (1 - scrollFactor * 0.5)})`);
            ctx.fillStyle = gradient;
            ctx.fill();
          } else if (shape.type === 'rect') {
            ctx.translate(shape.x, shape.y + parallaxOffset);
            ctx.rotate(((shape.rotation || 0) + rotationOffset) * Math.PI / 180);
            ctx.fillStyle = `rgba(255, 255, 255, ${shape.opacity * (1 - scrollFactor * 0.5)})`;
            ctx.fillRect(-shape.width / 2, -shape.height / 2, shape.width, shape.height);
          } else if (shape.type === 'line') {
            ctx.beginPath();
            ctx.moveTo(shape.x1, shape.y1 + parallaxOffset);
            ctx.lineTo(shape.x2, shape.y2 + parallaxOffset);
            ctx.strokeStyle = `rgba(255, 255, 255, ${shape.opacity * (1 - scrollFactor * 0.5)})`;
            ctx.lineWidth = shape.width || 2;
            ctx.stroke();
          }

          ctx.restore();
        });

        // Reset filter
        ctx.filter = 'none';
      });

      rafIdRef.current = requestAnimationFrame(draw);
    };

    // Scroll handler
    const handleScroll = () => {
      scrollYRef.current = window.scrollY;
    };

    // Resize handler
    const handleResize = () => {
      setCanvasSize();
      // Update layer positions based on new dimensions
      width = window.innerWidth;
      height = window.innerHeight;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    draw();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none"
      style={{ mixBlendMode: 'screen' }}
    />
  );
};

export default ScrollResponsiveHeroGraphic;